import React, { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  CheckIcon,
  LightbulbIcon,
  PencilIcon,
  PinIcon,
  PlusIcon,
  SettingsIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";

import { hasPermission } from "simplydo/core/permissions";
import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import {
  Dropdown,
  DropdownItem,
  DropdownItemLink,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { EntityToolbar } from "components/lib/EntityHeader";
import { ChallengeFollowButton } from "components/lib/FollowButton";

import AdjustableUserList from "./AdjustableUserList";
import ChallengeStageChanger from "./ChallengeStageChanger";

type ChallengeStatusBarProps = {
  challengeId: string;
  completed?: {
    audienceCompleted: "done" | "almost" | false;
    templateCompleted: "done" | "almost" | false;
    detailsCompleted: "done" | "almost" | false;
    announcementCompleted: "done" | "almost" | false;
  };
  newIdea: () => void;
  myIdeas: OpenAPI.Schemas["Idea"][];
  overviewPage: boolean;
  redirectToLogin: (orgCode?: string) => void;
};

const ChallengeStatusBar = ({
  challengeId,
  completed,
  newIdea,
  myIdeas,
  overviewPage,
  redirectToLogin,
}: ChallengeStatusBarProps) => {
  const [requestContext, setRequestContext] = useState("");
  const user = useCurrentUser();
  const { t } = useTranslation();
  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [challengeId]),
  );
  const challenge = challengeQuery.data;

  const [template, setTemplate] = useState({});
  const [requestToOpenChallengeOpen, setRequestToOpenChallengeOpen] =
    useState(false);
  const [selectedRequestAdmin, setSelectedRequestAdmin] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [admins, setAdmins] = useState<OpenAPI.Schemas["User"][]>([]);
  const [contactingAdmin, setContactingAdmin] = useState(false);
  const [announcements, setAnnouncements] = useState<
    OpenAPI.Schemas["Announcement"][]
  >([]);
  const theme = useTheme();

  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );
  const canManageAnnouncements = hasPermission(
    user,
    "challenge.manageAnnouncements",
    challengeId,
  );

  const getChallenge = useCallback(() => {
    if (canManageChallenge || canManageAnnouncements) {
      api.announcements.get(
        "challenge",
        challengeId,
        1,
        (data) => setAnnouncements(data.automaticAnnouncements),
        () => {},
      );
    }
    api.challenges.getTemplate(
      challengeId,
      (newTemplate) => setTemplate(newTemplate),
      () => {},
    );
  }, [challengeId, canManageChallenge, canManageAnnouncements]);

  useEffect(() => getChallenge(), [getChallenge]);

  const userOrg = user?.ownerOrganisation?._id;
  const getAdmins = useCallback(() => {
    if (userOrg) {
      setAdminsLoading(true);
      api.organisations.getAdmins(
        userOrg,
        { limit: 10, permissions: ["org.publishChallenges"] },
        ({ users }) => {
          setAdmins(users);
          setAdminsLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setAdminsLoading(false);
        },
      );
    }
  }, [userOrg]);

  const contactAdmin = useCallback(() => {
    if (!userOrg) return;
    setContactingAdmin(true);
    api.organisations.contactAdmin(
      userOrg,
      {
        challenge: challengeId,
        contactType: "requestOpenChallenge",
        admin: selectedRequestAdmin,
        requestContext,
      },
      () => {
        toast.success("Request sent to admin");
        setSelectedRequestAdmin("");
        setContactingAdmin(false);
        setRequestToOpenChallengeOpen(false);
        setRequestSent(true);
      },
      (err) => {
        toast.error(err.message);
        setContactingAdmin(false);
      },
    );
  }, [selectedRequestAdmin, userOrg, challengeId, requestContext]);

  useEffect(() => {
    if (requestToOpenChallengeOpen) {
      getAdmins();
    }
  }, [requestToOpenChallengeOpen, getAdmins]);

  const updateStage = useCallback(
    (newStage: string, openWithComms = false) => {
      const updateStageFunction = () =>
        api.challenges.updateStage(
          challengeId,
          newStage,
          openWithComms,
          (s) => {
            api.setQueryData(
              api.challenges.get,
              [challengeId],
              (prevChallenge) => ({ ...(prevChallenge || {}), ...s }),
            );
          },
          (err) => {
            toast.error(err.message);
          },
        );

      if (newStage === "closed") {
        util
          .confirm(
            `Really close this ${t("generic.challenge")}?`,
            `Are you sure you want to close this ${t("generic.challenge")}. Closing the ${t("generic.challenge")} will prevent any new ${t("generic.ideas")} or submissions.`,
          )
          .then(() => {
            updateStageFunction();
          })
          .catch(() => {});
      } else updateStageFunction();
    },
    [challengeId, t],
  );

  const pinChallenge = useCallback(() => {
    api.challenges.setStatus(
      challengeId,
      { isPinned: !challenge?.isPinned },
      () => {
        api.setQueryData(
          api.challenges.get,
          [challengeId],
          (prevChallenge) => ({
            ...(prevChallenge || {}),
            isPinned: !challenge?.isPinned,
          }),
        );
      },
      (err) => {
        toast.error(err.message);
      },
    );
  }, [challengeId, challenge]);

  const canPublishChallenges = useMemo(
    () =>
      util.hasPermission(
        user,
        "org.publishChallenges",
        user?.ownerOrganisation?._id,
      ),
    [user],
  );
  const canMakeIdea = util.canCreateIdea(user, challenge);
  const myUnsubmittedIdeas = useMemo(
    () => myIdeas?.filter((idea) => !idea.isSubmitted),
    [myIdeas],
  );

  return (
    <EntityToolbar>
      <Modal
        open={requestToOpenChallengeOpen}
        onClose={() => setRequestToOpenChallengeOpen(false)}
      >
        <ModalHeader>Request to open {t("generic.challenge")}</ModalHeader>
        <ModalContent>
          <ModalDescription>
            <p>
              You do not have permission to open {t("generic.challenges")} in
              your organisation. Please select an admin, who will receive a
              request from you to open this {t("generic.challenge")}.
            </p>
          </ModalDescription>
          {adminsLoading ? (
            <Loader />
          ) : (
            <div>
              <AdjustableUserList
                results={admins}
                actions={(adminUser) => (
                  <Checkbox
                    checked={selectedRequestAdmin === adminUser._id}
                    onClick={() => {
                      setSelectedRequestAdmin((prevId) =>
                        prevId === adminUser._id ? "" : adminUser._id,
                      );
                    }}
                  />
                )}
              />
            </div>
          )}
          <p style={{ marginTop: 10 }}>
            Please add context to your request to help the admin understand why
            you want to open the {t("generic.challenge")}.
          </p>
          <Input
            fluid
            type="text"
            placeholder={`Why do you want to open this ${t("generic.challenge")}?`}
            defaultValue={requestContext}
            onChange={(e, { value }) => setRequestContext(value)}
          />
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setRequestToOpenChallengeOpen(false)}>
            <Icon icon={XIcon} />
            Cancel
          </Button>
          <Button
            disabled={!selectedRequestAdmin}
            loading={contactingAdmin}
            onClick={contactAdmin}
            color="primary"
          >
            <Icon icon={CheckIcon} />
            Send request
          </Button>
        </ModalActions>
      </Modal>

      {challenge ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
          }}
        >
          {theme.sizes.isComputer ? (
            <>
              {!challenge.stage && (
                <Label color="yellow">
                  {t("challenge.stages.draft.title")}
                </Label>
              )}
              {challenge.stage === "published" && (
                <Label color="green">
                  {t("challenge.stages.published.title")}
                </Label>
              )}
              {challenge.stage === "closed" && (
                <Label color="red">{t("generic.closed")}</Label>
              )}
            </>
          ) : null}

          {theme.sizes.isComputer &&
          (canManageChallenge ||
            util.hasPermission(user, "challenge.viewSettings", challengeId)) ? (
            <React.Fragment>
              <ButtonLink
                size="small"
                variant="outline"
                to={`/challenges/${challenge._id}/ideas`}
              >
                <Icon icon={LightbulbIcon} />
                {t("common:capitalise", { key: "generic.ideas" })}
                <b style={{ marginLeft: 4, fontWeight: 600 }}>
                  {challenge.ideaCount || 0}
                </b>
              </ButtonLink>
              <ButtonLink
                size="small"
                variant="outline"
                to={`/challenges/${challenge._id}/settings/audience`}
              >
                <Icon icon={UsersIcon} />
                {t("challenge.settings.audience.title")}
                <b style={{ marginLeft: 4, fontWeight: 600 }}>
                  {challenge.audienceSize || 0}
                </b>
              </ButtonLink>
            </React.Fragment>
          ) : null}

          <ChallengeFollowButton challenge={challenge} />
        </div>
      ) : null}
      {challengeQuery.isLoading || !challenge ? (
        <div />
      ) : (
        <div style={{ gap: 4 }}>
          {util.hasPermission(
            user,
            "org.manageChallenges",
            user?.ownerOrganisation?._id,
          ) && !theme.sizes.isMobile ? (
            <Popup
              on="hover"
              trigger={
                <Button size="small" onClick={pinChallenge} variant="ghost">
                  <Icon icon={PinIcon} />
                  {challenge.isPinned
                    ? t("challenge.ideas.actions.pin.unPin")
                    : t("challenge.ideas.actions.pin.pin")}
                </Button>
              }
            >
              {t("challenges.pin.info")}
            </Popup>
          ) : null}

          {canManageChallenge ||
          util.hasPermission(user, "challenge.viewSettings", challengeId) ? (
            !theme?.sizes?.isMobile ? (
              <React.Fragment>
                {window.location.pathname.match(
                  /\/challenges\/[a-zA-Z0-9-]+\/settings/,
                ) && (
                  <ButtonLink
                    size="small"
                    variant="ghost"
                    to={`/challenges/${challenge._id}`}
                  >
                    <Icon icon={ArrowLeftIcon} />
                    {t("challenge.settings.viewChallenge")}
                  </ButtonLink>
                )}
                {!window.location.pathname.match(
                  /\/challenges\/[a-zA-Z0-9-]+\/settings/,
                ) && (
                  <ButtonLink
                    size="small"
                    variant="ghost"
                    to={`/challenges/${challenge._id}/settings`}
                  >
                    <Icon icon={SettingsIcon} />
                    {t("challenge.settings.title")}
                  </ButtonLink>
                )}
                {!window.location.pathname.match(
                  /\/challenges\/[a-zA-Z0-9-]+\/format/,
                ) &&
                  (canManageChallenge ||
                    util.hasPermission(
                      user,
                      "challenge.editIdeaFormat",
                      challengeId,
                    )) && (
                    <ButtonLink
                      size="small"
                      variant="ghost"
                      to={`/challenges/${challenge._id}/settings/format`}
                    >
                      <Icon icon={PencilIcon} />
                      {t("challenge.editFormat")}
                    </ButtonLink>
                  )}
                {canManageChallenge ||
                util.hasPermission(
                  user,
                  "challenge.editSettings",
                  challengeId,
                ) ? (
                  canPublishChallenges ? (
                    <ChallengeStageChanger
                      challengeId={challengeId}
                      updateStage={updateStage}
                      completed={completed}
                      announcements={announcements}
                      template={template}
                    />
                  ) : challenge.stage !== "published" ? (
                    <Button
                      size="small"
                      disabled={requestSent}
                      onClick={() => setRequestToOpenChallengeOpen(true)}
                      color="primary"
                    >
                      <Icon icon={requestSent ? CheckIcon : undefined} />
                      {t(
                        `challenge.stages.draft.request${requestSent ? "Sent" : ""}`,
                      )}
                    </Button>
                  ) : null
                ) : null}
              </React.Fragment>
            ) : (
              <Dropdown
                direction="left"
                trigger={
                  <Button size="small" variant="ghost">
                    <Icon icon={SettingsIcon} />
                  </Button>
                }
              >
                <DropdownMenu>
                  {!window.location.pathname.match(
                    /\/challenges\/[a-zA-Z0-9-]+\/format/,
                  ) &&
                    (canManageChallenge ||
                      util.hasPermission(
                        user,
                        "challenge.editIdeaFormat",
                        challengeId,
                      )) && (
                      <DropdownItemLink
                        to={`/challenges/${challenge._id}/settings/format`}
                      >
                        <Icon icon={PencilIcon} />
                        {t("challenge.editFormat")}
                      </DropdownItemLink>
                    )}
                  {window.location.pathname.match(
                    /\/challenges\/[a-zA-Z0-9-]+\/settings/,
                  ) && (
                    <DropdownItemLink to={`/challenges/${challenge._id}`}>
                      <Icon icon={ArrowLeftIcon} />
                      {t("challenge.settings.viewChallenge")}
                    </DropdownItemLink>
                  )}
                  {!window.location.pathname.match(
                    /\/challenges\/[a-zA-Z0-9-]+\/settings/,
                  ) && (
                    <DropdownItemLink
                      to={`/challenges/${challenge._id}/settings`}
                    >
                      <Icon icon={SettingsIcon} />
                      {t("challenge.settings.title")}
                    </DropdownItemLink>
                  )}
                  {canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.editSettings",
                    challengeId,
                  ) ? (
                    <DropdownItem onClick={pinChallenge}>
                      <Icon icon={PinIcon} />
                      {challenge.isPinned
                        ? `Un-pin ${t("generic.challenge")}`
                        : `Pin ${t("generic.challenge")}`}
                    </DropdownItem>
                  ) : null}
                  {canPublishChallenges ? (
                    <>
                      {!challenge.stage && (
                        <DropdownItem onClick={() => updateStage("published")}>
                          {t("challenge.stages.draft.open")}
                        </DropdownItem>
                      )}
                      {challenge.stage === "published" && (
                        <DropdownItem onClick={() => updateStage("closed")}>
                          {t("challenge.stages.published.close")}
                        </DropdownItem>
                      )}
                      {challenge.stage === "closed" && (
                        <DropdownItem onClick={() => updateStage("published")}>
                          {t("challenge.stages.closed.reOpen")}
                        </DropdownItem>
                      )}
                    </>
                  ) : null}
                </DropdownMenu>
              </Dropdown>
            )
          ) : (
            !theme?.sizes?.isMobile &&
            overviewPage && (
              <div>
                {challenge?.stage === "published" && !user ? (
                  <>
                    {(!myUnsubmittedIdeas ||
                      myUnsubmittedIdeas?.length === 0) &&
                    canMakeIdea ? (
                      <Button
                        size="small"
                        onClick={
                          canMakeIdea ? newIdea : () => redirectToLogin()
                        } // If public idea creation is enabled a user does not need to be logged in
                        color="primary"
                      >
                        <Icon icon={PlusIcon} />
                        {myIdeas?.length === 0
                          ? challenge.buttonText
                            ? challenge.buttonText
                            : t("challenge.ideas.createYourIdea")
                          : t("challenge.startNew")}
                      </Button>
                    ) : null}

                    {myUnsubmittedIdeas?.length === 1 && (
                      <ButtonLink
                        size="small"
                        color="primary"
                        to={`/ideas/${myUnsubmittedIdeas[0]!._id}`}
                      >
                        <Icon icon={LightbulbIcon} />
                        {t("challenge.continueIdea")}
                      </ButtonLink>
                    )}

                    {myUnsubmittedIdeas?.length > 1 && (
                      <Dropdown
                        trigger={
                          <Button
                            size="small"
                            style={{ width: "100%" }}
                            color="primary"
                          >
                            <Icon icon={LightbulbIcon} />
                            {t("challenge.continueIdea")}
                          </Button>
                        }
                      >
                        <DropdownMenu>
                          {myUnsubmittedIdeas.map((i) => (
                            <DropdownItemLink
                              key={i._id}
                              to={`/ideas/${i._id}`}
                            >
                              {i.name}
                            </DropdownItemLink>
                          ))}
                        </DropdownMenu>
                      </Dropdown>
                    )}
                  </>
                ) : null}
              </div>
            )
          )}
        </div>
      )}
    </EntityToolbar>
  );
};

export default ChallengeStatusBar;
